import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Printer,
  FileDown,
  Copy,
  CheckCheck,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Settings,
  Grid,
  CheckCircle,
} from "lucide-react";

const GenerateCardsPage = () => {
  // State for card generation options
  const [numberOfCards, setNumberOfCards] = useState(4);
  const [gameType, setGameType] = useState("75");
  const [cardSize, setCardSize] = useState("standard");
  const [includeHeader, setIncludeHeader] = useState(true);
  const [includeFooter, setIncludeFooter] = useState(true);
  const [freeSpace, setFreeSpace] = useState(true);
  const [generatedCards, setGeneratedCards] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  // Generate unique card ID
  const generateCardId = () => {
    return `card-${Math.random().toString(36).substring(2, 15)}`;
  };

  // Game type options with proper column letters - CORRECTED 75-ball and 90-ball configurations
  const gameTypeOptions = {
    15: {
      name: "15-Ball Express Bingo",
      icon: "🏎️",
      cols: 5,
      rows: 3,
      headers: ["B", "I", "N", "G", "O"],
      ranges: [
        [1, 3], // B: 1-3
        [4, 6], // I: 4-6
        [7, 9], // N: 7-9
        [10, 12], // G: 10-12
        [13, 15], // O: 13-15
      ],
    },
    30: {
      name: "30-Ball Speed Bingo",
      icon: "⚡",
      cols: 5, // Changed from 3 to 5 columns (B-I-N-G-O)
      rows: 3, // Keep 3 rows for 30-ball
      headers: ["B", "I", "N", "G", "O"],
      ranges: [
        [1, 6], // B: 1-6
        [7, 12], // I: 7-12
        [13, 18], // N: 13-18
        [19, 24], // G: 19-24
        [25, 30], // O: 25-30
      ],
    },
    50: {
      name: "50-Ball Quick Bingo",
      icon: "🔄",
      cols: 5,
      rows: 5,
      headers: ["B", "I", "N", "G", "O"],
      ranges: [
        [1, 10],
        [11, 20],
        [21, 30],
        [31, 40],
        [41, 50],
      ],
    },
    75: {
      name: "75-Ball American Bingo",
      icon: "🇺🇸",
      cols: 5,
      rows: 5, // Standard 5×5 grid for American bingo
      headers: ["B", "I", "N", "G", "O"],
      ranges: [
        [1, 15], // B: 1-15
        [16, 30], // I: 16-30
        [31, 45], // N: 31-45
        [46, 60], // G: 46-60
        [61, 75], // O: 61-75
      ],
    },
    90: {
      name: "90-Ball British Bingo",
      icon: "🇬🇧",
      cols: 9,
      rows: 3, // Traditional 3-row ticket for British bingo
      headers: ["1", "2", "3", "4", "5", "6", "7", "8", "9"],
      ranges: [
        [1, 10],
        [11, 20],
        [21, 30],
        [31, 40],
        [41, 50],
        [51, 60],
        [61, 70],
        [71, 80],
        [81, 90],
      ],
    },
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Generate a single bingo card - FIXED ALL CARD TYPES
  const generateSingleCard = (gameTypeObj) => {
    const { cols, rows, ranges, headers } = gameTypeObj;

    // Initialize empty card grid
    const card = Array(rows)
      .fill()
      .map(() => Array(cols).fill(null));

    // Find the middle cell coordinates for free space placement
    const middleRow = Math.floor(rows / 2);
    const middleCol = Math.floor(cols / 2);

    // Special handling for 90-ball British bingo
    if (gameType === "90") {
      // Step 1: For each row, decide which columns will have numbers (5 out of 9)
      const filledPositions = [];

      for (let row = 0; row < rows; row++) {
        // Create array of column indices [0,1,2,3,4,5,6,7,8]
        const allColumns = Array.from({ length: cols }, (_, i) => i);

        // Shuffle the columns
        allColumns.sort(() => Math.random() - 0.5);

        // Select 5 columns for this row
        const selectedColumns = allColumns.slice(0, 5).sort((a, b) => a - b);

        // Add selected positions to our tracking array
        selectedColumns.forEach((col) => {
          filledPositions.push({ row, col });
        });
      }

      // If we want a free space, replace one of the filled positions
      let freeSpacePosition = null;
      if (freeSpace && filledPositions.length > 0) {
        const randomIndex = Math.floor(Math.random() * filledPositions.length);
        freeSpacePosition = filledPositions[randomIndex];
      }

      // Step 2: Generate numbers for each column, respecting the specific ranges
      // Keep track of used numbers in each column
      const usedInColumn = Array(cols)
        .fill()
        .map(() => []);

      // Fill the selected positions with appropriate numbers
      filledPositions.forEach(({ row, col }) => {
        // Skip this position if it's designated as the free space
        if (
          freeSpacePosition &&
          freeSpacePosition.row === row &&
          freeSpacePosition.col === col
        ) {
          card[row][col] = "FREE";
          return;
        }

        // Get the range for this column
        const min = ranges[col][0];
        const max = ranges[col][1];

        // Generate a random number that hasn't been used in this column yet
        let number;
        do {
          number = Math.floor(Math.random() * (max - min + 1)) + min;
        } while (usedInColumn[col].includes(number));

        // Add the number to the card and track it
        card[row][col] = number;
        usedInColumn[col].push(number);
      });

      // Step 3: Sort numbers within each column
      usedInColumn.forEach((numbers, col) => {
        // Sort numbers in ascending order
        numbers.sort((a, b) => a - b);

        // Update the card with sorted numbers
        let index = 0;
        for (let row = 0; row < rows; row++) {
          if (card[row][col] !== null && card[row][col] !== "FREE") {
            card[row][col] = numbers[index++];
          }
        }
      });
    }
    // For all other card types (15, 30, 50, 75-ball bingo)
    else {
      // Fill each column with numbers according to the specified range
      for (let col = 0; col < cols; col++) {
        const min = ranges[col][0];
        const max = ranges[col][1];

        // Generate all possible numbers for this column
        const availableNumbers = [];
        for (let i = min; i <= max; i++) {
          availableNumbers.push(i);
        }

        // Shuffle the numbers
        availableNumbers.sort(() => Math.random() - 0.5);

        // Determine how many numbers we need for this column
        let numbersNeeded = rows;

        // If this is the center column and we want a free space, reduce by 1
        if (freeSpace && col === middleCol && rows % 2 === 1) {
          numbersNeeded = rows - 1;
        }

        // Take only as many numbers as we need
        const selectedNumbers = availableNumbers.slice(0, numbersNeeded);

        // Sort the numbers in ascending order for this column
        selectedNumbers.sort((a, b) => a - b);

        // Place the numbers in the column
        let numberIndex = 0;
        for (let row = 0; row < rows; row++) {
          // Place FREE space in the middle if applicable
          if (freeSpace && row === middleRow && col === middleCol) {
            card[row][col] = "FREE";
          }
          // Otherwise place the next number
          else if (numberIndex < selectedNumbers.length) {
            card[row][col] = selectedNumbers[numberIndex++];
          }
        }
      }
    }

    return {
      id: generateCardId(),
      grid: card,
      type: gameType,
      headers: headers,
    };
  };

  // Handle card generation
  const generateCards = () => {
    setIsGenerating(true);
    setGeneratedCards([]);

    const gameTypeObj = gameTypeOptions[gameType];

    // Simulate a brief loading period
    setTimeout(() => {
      const newCards = Array(parseInt(numberOfCards))
        .fill()
        .map(() => generateSingleCard(gameTypeObj));

      setGeneratedCards(newCards);
      setIsGenerating(false);
    }, 500);
  };

  // Print the cards
  const printCards = () => {
    window.print();
  };

  // Format the card headers
  const getCardHeaders = (card) => {
    return card.headers;
  };

  // Handle "Copy Link" functionality
  const handleCopyLink = () => {
    const params = new URLSearchParams({
      count: numberOfCards,
      type: gameType,
      size: cardSize,
      header: includeHeader ? 1 : 0,
      footer: includeFooter ? 1 : 0,
      free: freeSpace ? 1 : 0,
    });

    const sharableUrl = `${
      window.location.origin
    }/generate-bingo-cards?${params.toString()}`;
    navigator.clipboard.writeText(sharableUrl);

    setShowCopiedMessage(true);
    setTimeout(() => setShowCopiedMessage(false), 2000);
  };

  // Delete a specific card
  const deleteCard = (cardId) => {
    setGeneratedCards(generatedCards.filter((card) => card.id !== cardId));
  };

  // Download cards as PDF (this is a placeholder - actual PDF generation would require a library)
  const downloadCards = () => {
    alert(
      "PDF download functionality would be implemented here with a library like jsPDF."
    );
  };

  // Format cell display value (add column letter prefix for visual display) - ENHANCED to support 90-ball bingo display
  const formatCellValue = (value, colIndex, type) => {
    if (value === null) return "";
    if (value === "FREE") return "FREE";

    // For 90-ball bingo, just show the number without column prefix
    if (type === "90") {
      return value;
    }

    // For all other bingo types, show letter-number combo
    const headers = gameTypeOptions[type].headers;
    return `${headers[colIndex]}${value}`;
  };

  return (
    <>
      <Helmet>
        <title>Generate Printable Bingo Cards | Free Bingo Card Creator</title>
        <meta
          name="description"
          content="Create and print custom bingo cards for free. Choose from different sizes and game types including 75-ball, 90-ball, and speed bingo."
        />
        <meta
          name="keywords"
          content="bingo cards, printable bingo cards, bingo card generator, free bingo cards, custom bingo cards"
        />
        <meta
          property="og:title"
          content="Generate Printable Bingo Cards | Free Bingo Card Creator"
        />
        <meta
          property="og:description"
          content="Create and print your own custom bingo cards for free. Perfect for parties, classroom activities, or bingo nights."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://callmybingo.com/bingo-cards-generator"
        />
      </Helmet>

      <div className="bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              Bingo Cards Generator
            </h1>
            <p className="text-lg md:text-xl text-purple-100 max-w-2xl mx-auto">
              Create custom printable bingo cards for your next game night,
              classroom activity, or party!
            </p>
          </div>

          {/* Main Content Container */}
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden mb-10">
            {/* Breadcrumbs */}
            <div className="bg-gray-50 py-3 px-6 text-sm text-gray-500">
              <Link to="/" className="hover:text-purple-600 transition-colors">
                Home
              </Link>
              <span className="mx-2">&gt;</span>
              <span className="text-purple-800 font-medium">
                Generate Bingo Cards
              </span>
            </div>

            {/* Card Generator Controls */}
            <div className="p-6 md:p-8">
              <div className="max-w-3xl mx-auto">
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 mb-8">
                  <h2 className="text-2xl font-bold text-purple-800 mb-6 flex items-center">
                    <Settings className="mr-3 text-purple-600" />
                    Bingo Card Generation Options
                  </h2>

                  <div className="space-y-6">
                    {/* Game Type Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Game Type
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.entries(gameTypeOptions).map(
                          ([value, { name, icon }]) => (
                            <button
                              key={value}
                              onClick={() => setGameType(value)}
                              className={`flex items-center p-3 border rounded-md ${
                                gameType === value
                                  ? "border-purple-500 bg-purple-50 text-purple-800"
                                  : "border-gray-300 hover:border-purple-300"
                              }`}
                            >
                              <span className="text-2xl mr-3">{icon}</span>
                              <div className="text-left">
                                <div className="font-medium">{name}</div>
                                <div className="text-xs text-gray-500">
                                  {value} numbers
                                </div>
                              </div>
                              {gameType === value && (
                                <CheckCircle
                                  size={16}
                                  className="ml-auto text-purple-500"
                                />
                              )}
                            </button>
                          )
                        )}
                      </div>
                    </div>

                    {/* Number of Cards */}
                    <div>
                      <label
                        htmlFor="card-count"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Number of Cards to Generate
                      </label>
                      <input
                        type="number"
                        id="card-count"
                        min="1"
                        max="100"
                        value={numberOfCards}
                        onChange={(e) =>
                          setNumberOfCards(parseInt(e.target.value) || 1)
                        }
                        className="w-full max-w-xs border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Generate between 1 and 100 unique cards
                      </p>
                    </div>

                    {/* Advanced Settings Toggle */}
                    <div>
                      <button
                        onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                        className="flex items-center text-sm text-purple-700 font-medium"
                      >
                        {isAdvancedOpen ? (
                          <ChevronUp size={16} className="mr-1" />
                        ) : (
                          <ChevronDown size={16} className="mr-1" />
                        )}
                        Advanced Options
                      </button>

                      {isAdvancedOpen && (
                        <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                          {/* Card Size */}
                          <fieldset>
                            <legend className="text-sm font-medium text-gray-700">
                              Card Size
                            </legend>
                            <div className="mt-2 space-y-2">
                              <div className="flex items-center">
                                <input
                                  id="standard"
                                  name="cardSize"
                                  type="radio"
                                  checked={cardSize === "standard"}
                                  onChange={() => setCardSize("standard")}
                                  className="h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                />
                                <label
                                  htmlFor="standard"
                                  className="ml-3 text-sm text-gray-700"
                                >
                                  Standard (4 cards per page)
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  id="large"
                                  name="cardSize"
                                  type="radio"
                                  checked={cardSize === "large"}
                                  onChange={() => setCardSize("large")}
                                  className="h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                />
                                <label
                                  htmlFor="large"
                                  className="ml-3 text-sm text-gray-700"
                                >
                                  Large (1 card per page)
                                </label>
                              </div>
                            </div>
                          </fieldset>

                          {/* Headers, Footers, Free Space */}
                          <div className="flex flex-wrap gap-6">
                            <div className="flex items-center">
                              <input
                                id="include-header"
                                type="checkbox"
                                checked={includeHeader}
                                onChange={() =>
                                  setIncludeHeader(!includeHeader)
                                }
                                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor="include-header"
                                className="ml-2 text-sm text-gray-700"
                              >
                                Include Header
                              </label>
                            </div>
                            <div className="flex items-center">
                              <input
                                id="include-footer"
                                type="checkbox"
                                checked={includeFooter}
                                onChange={() =>
                                  setIncludeFooter(!includeFooter)
                                }
                                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor="include-footer"
                                className="ml-2 text-sm text-gray-700"
                              >
                                Include Footer
                              </label>
                            </div>

                            {/* Show free space option for all game types */}
                            <div className="flex items-center">
                              <input
                                id="free-space"
                                type="checkbox"
                                checked={freeSpace}
                                onChange={() => setFreeSpace(!freeSpace)}
                                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor="free-space"
                                className="ml-2 text-sm text-gray-700"
                              >
                                Free Space in Center
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Generate Button */}
                    <div className="pt-4">
                      <button
                        onClick={generateCards}
                        disabled={isGenerating}
                        className="w-full sm:w-auto px-6 py-3 bg-purple-600 text-white text-lg font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                      >
                        {isGenerating ? (
                          <>
                            <RefreshCw size={20} className="animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Grid size={20} />
                            Generate {numberOfCards} Cards
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Generated Cards */}
                {generatedCards.length > 0 && (
                  <div className="mb-10">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-gray-800">
                        Your Bingo Cards
                      </h2>
                      <div className="flex gap-2">
                        <button
                          onClick={printCards}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        >
                          <Printer size={16} className="mr-1" />
                          Print
                        </button>
                        <button
                          onClick={downloadCards}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        >
                          <FileDown size={16} className="mr-1" />
                          Download PDF
                        </button>
                        <button
                          onClick={handleCopyLink}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        >
                          {showCopiedMessage ? (
                            <>
                              <CheckCheck
                                size={16}
                                className="mr-1 text-green-500"
                              />
                              <span className="text-green-500">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy size={16} className="mr-1" />
                              <span>Share</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Print-ready cards */}
                    <div className="print-container">
                      <div
                        className={`cards-grid ${
                          cardSize === "large" ? "large" : "standard"
                        }`}
                      >
                        {generatedCards.map((card) => (
                          <div key={card.id} className="print-card">
                            {/* Non-printable actions */}
                            <div className="card-actions no-print">
                              <button
                                onClick={() => deleteCard(card.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Remove this card"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>

                            {/* Card content */}
                            <div className="bingo-card">
                              {includeHeader && (
                                <div className="card-header">
                                  <h3>BINGO</h3>
                                </div>
                              )}

                              <div className="card-body">
                                {/* Column headers */}
                                <div className="card-row header-row">
                                  {getCardHeaders(card).map((header, i) => (
                                    <div
                                      key={`header-${i}`}
                                      className="card-cell header-cell"
                                    >
                                      {header}
                                    </div>
                                  ))}
                                </div>

                                {/* Card numbers */}
                                {card.grid.map((row, rowIndex) => (
                                  <div
                                    key={`row-${rowIndex}`}
                                    className="card-row"
                                  >
                                    {row.map((cell, colIndex) => (
                                      <div
                                        key={`cell-${rowIndex}-${colIndex}`}
                                        className={`card-cell ${
                                          cell === "FREE"
                                            ? "free-space"
                                            : cell === null
                                            ? "empty-cell"
                                            : ""
                                        }`}
                                      >
                                        {formatCellValue(
                                          cell,
                                          colIndex,
                                          card.type
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>

                              {includeFooter && (
                                <div className="card-footer">
                                  <p>callmybingo.com</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print-only styles */}
      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-container,
          .print-container * {
            visibility: visible;
          }
          .no-print {
            display: none;
          }
          .print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .bingo-card {
            page-break-inside: avoid;
            margin: 0;
            border: 1px solid #000;
          }
        }

        /* Card grid layouts */
        .cards-grid {
          display: grid;
          gap: 1rem;
        }
        .cards-grid.standard {
          grid-template-columns: repeat(2, 1fr);
        }
        .cards-grid.large {
          grid-template-columns: repeat(1, 1fr);
        }

        /* Card styling */
        .bingo-card {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          overflow: hidden;
        }
        .card-header {
          background-color: #8b5cf6;
          color: white;
          text-align: center;
          padding: 0.5rem;
          font-weight: bold;
        }
        .card-footer {
          background-color: #f3f4f6;
          color: #4b5563;
          text-align: center;
          padding: 0.25rem;
          font-size: 0.75rem;
        }
        .card-body {
          display: flex;
          flex-direction: column;
        }
        .card-row {
          display: flex;
        }
        .card-cell {
          flex: 1;
          text-align: center;
          border: 1px solid #e5e7eb;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 2rem;
          min-height: 2.5rem;
        }
        .header-cell {
          font-weight: bold;
          background-color: #f3f4f6;
        }
        .free-space {
          background-color: #e0e7ff;
          font-weight: bold;
        }
        .empty-cell {
          background-color: #f9fafb;
        }
        .card-actions {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          z-index: 10;
        }
        .print-card {
          position: relative;
        }

        /* Responsive adjustments */
        @media (max-width: 640px) {
          .cards-grid.standard {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

export default GenerateCardsPage;
